/**
 * SourceFilePicker.tsx — modal that lists a project's runnable source files.
 *
 * The agent modal's Code tab opens this to pick the module a code-mode agent
 * invokes (the dotted path becomes `AgentConfig.target`, resolved against the
 * project's `agents.codeRoot` via `importlib.import_module`). Lists `*.py`
 * under the project's `src/` — the only kind the runner can invoke today.
 *
 * Reuses the `picker-*` CSS classes from LibraryModal so the search + keyboard
 * UX matches the rest of the app. Only active when a project backend
 * (`RemoteProjectAdapter`) is wired; local-mode projects have no source files.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { FilePy, MagnifyingGlass, SpinnerGap, X } from '@phosphor-icons/react';

import type { RemoteProjectAdapter, SourceFile } from '../projects/RemoteProjectAdapter';

interface SourceFilePickerProps {
  projectId: string;
  projectAdapter?: RemoteProjectAdapter;
  onClose: () => void;
  /** Receive the chosen dotted module path (writes to `AgentConfig.target`). */
  onSelect: (module: string) => void;
}

export function SourceFilePicker({
  projectId,
  projectAdapter,
  onClose,
  onSelect,
}: SourceFilePickerProps): JSX.Element {
  const [files, setFiles] = useState<SourceFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 10);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!projectAdapter) {
      setLoading(false);
      setError('No project backend connected — source files are only available for backend projects.');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    projectAdapter
      .listSrcFiles(projectId)
      .then((result) => {
        if (!cancelled) {
          setFiles(result);
          setLoading(false);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [projectAdapter, projectId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return files;
    return files.filter(
      (f) => f.module.toLowerCase().includes(q) || f.name.toLowerCase().includes(q),
    );
  }, [files, query]);

  // Keep the active row in range as the filter narrows.
  useEffect(() => {
    setActive((i) => (filtered.length === 0 ? 0 : Math.min(i, filtered.length - 1)));
  }, [filtered.length]);

  const choose = (file: SourceFile) => {
    onSelect(file.module);
    onClose();
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const sel = filtered[active];
      if (sel) choose(sel);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="picker"
        role="dialog"
        aria-modal="true"
        aria-label="Pick a source file"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKey}
      >
        <header className="picker-header">
          <MagnifyingGlass size={16} className="picker-search-icon" />
          <input
            ref={inputRef}
            className="picker-input"
            type="text"
            placeholder="Search source files…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
          {loading ? (
            <SpinnerGap size={16} className="picker-spinner" />
          ) : query ? (
            <button
              type="button"
              className="picker-clear"
              aria-label="Clear search"
              onClick={() => {
                setQuery('');
                inputRef.current?.focus();
              }}
            >
              <X size={14} />
            </button>
          ) : null}
          <button type="button" className="picker-close" aria-label="Close" onClick={onClose}>
            <X size={16} />
          </button>
        </header>

        <div className="picker-body" ref={listRef}>
          <PickerContent
            files={filtered}
            loading={loading}
            error={error}
            active={active}
            query={query}
            onHover={setActive}
            onSelect={choose}
          />
        </div>

        <footer className="picker-footer">
          <span className="hint">
            <kbd>↑</kbd> <kbd>↓</kbd> navigate · <kbd>↵</kbd> open · <kbd>Esc</kbd> close
          </span>
          <span className="picker-count">
            {filtered.length} file{filtered.length === 1 ? '' : 's'}
          </span>
        </footer>
      </div>
    </div>
  );
}

/** Body states: loading skeletons, error, empty project, no-results, or the list. */
function PickerContent({
  files,
  loading,
  error,
  active,
  query,
  onHover,
  onSelect,
}: {
  files: SourceFile[];
  loading: boolean;
  error: string | null;
  active: number;
  query: string;
  onHover: (i: number) => void;
  onSelect: (file: SourceFile) => void;
}) {
  if (loading) {
    return (
      <div className="picker-skeletons">
        {Array.from({ length: 4 }).map((_, i) => (
          <div className="picker-skeleton" key={i} />
        ))}
      </div>
    );
  }
  if (error) {
    return (
      <div className="picker-empty">
        <div className="picker-empty-title">Can’t list files</div>
        <div className="picker-empty-sub">{error}</div>
      </div>
    );
  }
  if (files.length === 0) {
    return (
      <div className="picker-empty">
        {query ? (
          <>
            <div className="picker-empty-title">No matches</div>
            <div className="picker-empty-sub">No source files match “{query}”.</div>
          </>
        ) : (
          <>
            <div className="picker-empty-title">No source files</div>
            <div className="picker-empty-sub">
              This project has no <code>*.py</code> files under its <code>src/</code>.
            </div>
          </>
        )}
      </div>
    );
  }
  return (
    <ul className="picker-list">
      {files.map((f, i) => (
        <li key={f.path}>
          <button
            type="button"
            className={`picker-row${i === active ? ' selected' : ''}`}
            onMouseEnter={() => onHover(i)}
            onClick={() => onSelect(f)}
          >
            <FilePy size={16} weight="regular" />
            <span className="picker-row-main">
              <span className="picker-row-title">{f.name}</span>
              <span className="picker-row-sub">{f.module}</span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
